import { formatToPKR } from "@/lib/helper";
import { TransactionTable } from "@/types/pos/sell";

interface SalesSummaryProps {
  data: TransactionTable;
  isLoading: boolean;
}

export default function SalesSummary({ data, isLoading }: SalesSummaryProps) {
  // Get payment methods from API response (only if it exists)
  const paymentMethods = data?.summary?.payment_methods;

  // Filter out zero values only (only if payment_methods exists)
  const filteredPaymentMethods = paymentMethods
    ? Object.entries(paymentMethods)
        .filter(([method, amount]) => amount > 0)
        .sort(([, a], [, b]) => b - a) // Sort by amount descending
    : [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-md p-3 h-20"></div>
          ))}
        </div>
        {/* Only show payment methods skeleton if payment_methods exists in data */}
        {data?.summary?.payment_methods && (
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-100 rounded-md p-2 h-16"></div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Financial summary cards (only show if value > 0)
  const financialCards = [
    {
      label: 'Total Sales',
      value: data?.summary?.total || 0,
      color: 'green',
      icon: 'fa-chart-line'
    },
    {
      label: 'Sub Total',
      value: data?.summary?.sub_total || 0,
      color: 'blue',
      icon: 'fa-calculator'
    },
    {
      label: 'Total Tax',
      value: data?.summary?.tax || 0,
      color: 'indigo',
      icon: 'fa-percentage'
    },
    {
      label: 'Total Refunds',
      value: data?.summary?.refund_total || 0,
      color: 'red',
      icon: 'fa-undo'
    }
  ].filter(card => card.value > 0);

  const getColorClasses = (color: string) => {
    const colors = {
      green: 'bg-green-50 border-green-200 text-green-600',
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
      red: 'bg-red-50 border-red-200 text-red-600',
      purple: 'bg-purple-50 border-purple-200 text-purple-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600',
      teal: 'bg-teal-50 border-teal-200 text-teal-600',
      pink: 'bg-pink-50 border-pink-200 text-pink-600',
    };
    return colors[color as keyof typeof colors] || 'bg-gray-50 border-gray-200 text-gray-600';
  };

  const getPaymentMethodColor = (index: number): string => {
    const colors = ['purple', 'orange', 'teal', 'pink', 'blue', 'indigo'];
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-3">
      {/* Financial Summary - Compact Cards */}
      {financialCards.length > 0 && (
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
          {financialCards.map((card) => (
            <div
              key={card.label}
              className={`${getColorClasses(card.color)} border rounded-md p-2.5`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium">{card.label}</p>
                <i className={`fa-solid ${card.icon} text-xs`}></i>
              </div>
              <p className="text-lg font-bold leading-tight">
                {formatToPKR(card.value)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Payment Methods Summary - Compact Cards */}
      {filteredPaymentMethods.length > 0 && (
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {filteredPaymentMethods.map(([method, amount], index) => (
            <div
              key={method}
              className={`${getColorClasses(getPaymentMethodColor(index))} border rounded-md p-2.5`}
            >
              <p className="text-xs font-medium mb-1 truncate" title={method}>
                {method}
              </p>
              <p className="text-base font-bold leading-tight">
                {formatToPKR(amount)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
